// Phase 43 (item 3 / D22) — enriched "Cases" card for the client record.
//
// Replaces the plain ref-pill list. Each linked matter (regular Case + collective
// case) shows its STATUS + status-date (item 2, computed — never denormalized)
// and its unpaid PRO-FORMA amount due (item 3). A client-level total sums the
// pro-forma due across all the client's matters. Amounts are labelled "pro forma",
// never "debt"/"owed" (D20).
//
// ARCHIVED matters (D22): the app's loaded `ca`/`cc` exclude archived matters
// (GET /cases defaults archived=false), so this component fetches archived-only
// matters for THIS client on selection and merges them in. It is a stable,
// module-scope component (mounted like CddClientSection) so its per-client
// useEffect/useState survive App re-renders — the EList detail branch itself
// cannot hold hooks (the Bug-A inline-component anti-pattern).

import React, { useState, useEffect } from "react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import {
  matterAmountDue,
  clientRegularCases,
  clientCollectiveCases,
  clientMatterStanding,
  isOwed, // 191 E.2g — the half-cent rule, by name
} from "../lib/clientMatters.mjs";
import { FS } from "../lib/tokens";
import { fq } from "../utils"; // 211 §22 — the quantum's figure, as the members table prints it
import type { Translate } from "../lib/interestRefusal"; // 211 §22 — the card's own words, typed at the seam
// 141 §2.3 — the three things a matter row can be showing: the prospect badge
// on 140 §1.2's class, a per-matter client status on the roster's own pill,
// and a status INHERITED from the matter — muted, see the pill below
// [RULED 2026-08-27].
// 168B §1c — the three arms moved to `lib/clientStandingPill.tsx`, unchanged,
// because ruling 1 puts the same value on the Clients list and a second copy of
// them is the defect 141 §2.2 closed for this pill's own colours.
import { clientStandingPill } from "../lib/clientStandingPill";
import { memberNumbers, numbersShown, ownQuantum } from "../lib/memberRefs.mjs"; // 211 §22 — the client's numbers and own quantum in each collective case

const fmtDate = (d: any) => (d ? d.toISOString().slice(0, 10) : "");

// Merge active + archived rows of one matter kind, dedupe by id (archived fetch
// and the loaded array can overlap on ?archived=all-style edge cases).
function mergeById(active: any, archived: any) {
  const seen = new Set(active.map((r: any) => r.id));
  return [...active, ...archived.filter((r: any) => !seen.has(r.id))];
}

export default function ClientCasesCard({
  client,
  ca,
  cc,
  inv,
  nav,
  fc,
  canSeeMatterDue,
  statusLabel,
}: any) {
  const { t } = useTranslation();
  const [arch, setArch] = useState<any>({ cases: [], ccs: [] });

  useEffect(() => {
    if (!client?.id) {
      setArch({ cases: [], ccs: [] });
      return;
    }
    let dead = false;
    Promise.all([
      api.get("/cases?archived=1").catch(() => []),
      api.get("/collective-cases?archived=1").catch(() => []),
    ]).then(([acs, accs]) => {
      if (dead) return;
      setArch({
        cases: (Array.isArray(acs) ? acs : []).filter((c) =>
          (c.clIds || []).includes(client.id),
        ),
        ccs: (Array.isArray(accs) ? accs : []).filter((c2) =>
          (c2.clientLinks || []).some((lk: any) => lk.clId === client.id),
        ),
      });
    });
    return () => {
      dead = true;
    };
  }, [client?.id]);

  const money = fc || ((n: any) => n);
  const label = statusLabel || ((code: any) => code || "—");
  // Phase 43 CORRECTION P2 (item A) — per-matter amount-due visibility gate.
  const canDue = canSeeMatterDue || (() => true);

  const regular = mergeById(clientRegularCases(client.id, ca), arch.cases);
  const collective = mergeById(clientCollectiveCases(client.id, cc), arch.ccs);

  // Client total sums ONLY the matters this viewer may see figures for, and
  // tracks whether any were authorized (label-only otherwise).
  let total = 0;
  let anyAuth = false;
  for (const c of regular)
    if (canDue(c.id, null)) {
      anyAuth = true;
      total += matterAmountDue(c.id, "caseId", inv);
    }
  for (const c of collective)
    if (canDue(null, c.id)) {
      anyAuth = true;
      total += matterAmountDue(c.id, "ccId", inv);
    }

  const none = regular.length === 0 && collective.length === 0;

  // The amount-due LABEL is always shown; the NUMBER only for an authorized
  // viewer. A gated slot shows the muted label — never "—" (implies zero), never
  // a blank/hidden cell (D34/D35). 213 §8 [RULED 32] — so the two differ on
  // purpose: an authorized slot with nothing owed prints « 0,00 € » (muted),
  // since zero is what it knows; a gated one knows nothing and keeps its label —
  // a number there, zero included, would tell what the viewer may not see.
  const dueLabel = (
    <span
      style={{
        fontSize: FS.s9,
        color: "var(--tm)",
        fontStyle: "italic",
        whiteSpace: "nowrap",
      }}
      title={t("amount.due.label", { fallback: "Amount due" })}
    >
      {t("amount.due.label", { fallback: "Amount due" })}
    </span>
  );
  const dueCell = (due: any, authorized: any) =>
    authorized ? (
      <span
        style={{
          fontSize: FS.s10,
          fontVariantNumeric: "tabular-nums",
          color: isOwed(due) ? "var(--tx)" : "var(--tm)",
          whiteSpace: "nowrap",
        }}
        title={t("amount.due.label", { fallback: "Amount due" })}
      >
        {money(isOwed(due) ? due : 0)}
      </span>
    ) : (
      dueLabel
    );

  const matterRow = (c: any, isCC: any) => {
    const due = matterAmountDue(c.id, isCC ? "ccId" : "caseId", inv);
    // 212 §21 — each matter by its reference and its title, the title alone without one
    const matterName = [c.ref, c.title].filter(Boolean).join(" · ");
    /* 141 §2.3 — WHAT THIS CLIENT'S STANDING IN THIS MATTER IS, from the one
       function that owns the rule (clientMatters.mjs): their OWN status on the
       link if they have been given one, else the matter's own status. Before
       §2.1 the regular arm could only ever be the matter's, because a regular
       link had nowhere to carry a status. `fromLink` says which branch
       answered, so the two vocabularies can wear their own pills without this
       file re-deriving the choice. */
    const standing = clientMatterStanding(client.id, c);
    const lk = standing.link;
    // 211 §22 — the client's numbers and own quantum in THIS collective case (its place's), read typed once
    const place = isCC ? (lk as { contractNumbers?: unknown; quantum?: unknown } | null) : null;
    const nums = memberNumbers(place);
    const own = ownQuantum(place);
    const tt = t as Translate;
    const sd = standing.date;
    const st = standing.status;
    // Phase 43 CORRECTION (item 3) — the retained lifecycle date set, shown
    // under the row. 141 §2.3: five slots, not three, and for BOTH kinds of
    // matter — §2.1 gave `CaseClient` the same columns under the same names,
    // so a regular case's link has exactly the same dates to show.
    const lifecycle = lk
      ? [
          ["client.record.prospect", "Prospect", lk.prospectDate],
          ["client.record.joined", "Joined", lk.joinedDate],
          ["client.record.suspended", "Suspended", lk.suspendedDate],
          ["client.record.terminated", "Terminated", lk.terminatedDate],
          ["client.record.archived", "Archived", lk.archivedDate],
        ].filter((x) => x[2])
      : [];
    /* THE PILL. A TAGGED MATTER IS A PROSPECT BEFORE ANYTHING ELSE — 140 §1.2's
       recipe, tag tested first so a tagged row never reaches the status lookup,
       same `.status-prospect` class and same seeded key, no new string. Then
       the client's own status, in the link vocabulary and wearing the colours
       the roster and the parties pane give it. Then the matter's own — MUTED
       [RULED Laurent 2026-08-27]: a status the client OWNS renders in its
       colours, a status INHERITED from the matter renders in the absence pair
       the parties pane gives "No status" (ink --tm, ground --bi), because the
       two vocabularies overlap in wording — a case whose status is `pending`
       displays "Suspended", which is also a link status — and the muting is
       what tells a reader whose standing they are looking at. The label stays
       the matter's own (`label(st)`); only the ink says whose it is.
       ONE METRIC across all three: `.status` is `--fs-10` / weight 500, and
       StatusPill is told to match rather than the column being left ragged. */
    const pill = clientStandingPill({
      standing,
      isProspect: !!c.isProspect,
      t,
      inheritedLabel: st ? label(st) : "",
    });
    return (
      <React.Fragment key={(isCC ? "cc" : "ca") + c.id}>
        <div
          onClick={() => nav(isCC ? "cc" : "ca", c.id)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "3px 0",
            borderBottom: lifecycle.length ? "none" : "1px solid var(--bd)",
            cursor: "pointer",
            opacity: c.archived ? 0.62 : 1,
          }}
        >
          <span className="name-cell" style={{ flex: "1 1 auto", fontSize: FS.s11 }} title={matterName || undefined}>
            {matterName || "—"}
            {c.archived && (
              <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>
                {" · " +
                  t("client.record.archived_matter", { fallback: "archived" })}
              </span>
            )}
          </span>
          {/* 211 §22 [ADVISOR] — beside a collective case, the client's numbers in it and, when
              set, that case's own quantum; they are edited in the case (§17). */}
          {(nums.length > 0 || own !== null) && (
            <span
              data-member-refs=""
              style={{ fontSize: FS.s9, color: "var(--tm)", fontFamily: "'JetBrains Mono',monospace", whiteSpace: "nowrap" }}
              title={[
                nums.length ? tt("field.contractNumber", { fallback: "Contract Number" }) + " — " + numbersShown(nums) : "",
                own !== null ? tt("field.quantum", { fallback: "Quantum" }) + " — " + fq(own) : "",
              ].filter(Boolean).join(" · ")}
            >
              {[numbersShown(nums), own !== null ? fq(own) : ""].filter(Boolean).join(" · ")}
            </span>
          )}
          {pill}
          {sd && (
            <span
              style={{ fontSize: FS.s9, color: "var(--tm)", whiteSpace: "nowrap" }}
            >
              {fmtDate(sd)}
            </span>
          )}
          {dueCell(due, canDue(isCC ? null : c.id, isCC ? c.id : null))}
        </div>
        {lifecycle.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              padding: "0 0 3px 4px",
              borderBottom: "1px solid var(--bd)",
              fontSize: FS.s8,
              color: "var(--tm)",
            }}
          >
            {lifecycle.map(([k, fb, v]) => (
              <span key={k}>
                {t(k, { fallback: fb })}: {String(v).slice(0, 10)}
              </span>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="detail-card">
      <h4>{t("list.section.cases", { fallback: "Cases" })}</h4>
      {none ? (
        <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>
          {t("common.none", { fallback: "None" })}
        </span>
      ) : (
        <>
          {regular.length > 0 && <div>{regular.map((c) => matterRow(c, false))}</div>}
          {collective.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <h4 style={{ fontSize: FS.s11, margin: "0 0 4px" }}>
                {t("list.section.collective_cases", { fallback: "Collective Cases" })}
              </h4>
              {collective.map((c) => matterRow(c, true))}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              paddingTop: 6,
              borderTop: "2px solid var(--bd)",
              fontSize: FS.s10,
              fontWeight: 600,
            }}
          >
            <span>{t("client.record.total_due", { fallback: "Total pro forma due" })}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {anyAuth ? money(total) : dueLabel}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
